import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner, Card, Badge, InputGroup, NavItem } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getBearerToken } from '../../functions';
import { useAuth } from '../pages/auth/AuthProvider';
import AddAgent from './AddAgent';
import { Link } from 'react-router-dom';

const BookUmrah = ({ show, setShow, selectedPackage, existingBooking, onSuccess }) => {
  const handleClose = () => {
    setShow(false);
    setStep(1); // Reset step when closing
    setPassengers([]);
    setBookingConfig({
      roomType: '',
      roomQuantity: 1,
      adults: 1,
      children: 0,
      infants: 0
    });
    setSelectedPricing(null);
    // Reset agent booking states
    setBookForAgent(false);
    setAgentSearchTerm('');
    setAgentSearchResults([]);
    setAgentSelected(false);
    setSelectedAgentId(null);
    if (onSuccess) {
      onSuccess();
    }
  };
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Booking configuration
  const [bookingConfig, setBookingConfig] = useState({
    roomType: '',
    roomQuantity: 1,
    adults: 1,
    children: 0,
    infants: 0
  });

  const [passengers, setPassengers] = useState([]);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [existingFileNames, setExistingFileNames] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Agent booking states
  const [bookForAgent, setBookForAgent] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [agentSearchResults, setAgentSearchResults] = useState([]);
  const [agentSelected, setAgentSelected] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [addAgent, setAddAgent] = useState(false);

  // Room capacity helper
  const getRoomCapacity = (roomType) => {
    switch(roomType) {
      case 'double': return 2;
      case 'triple': return 3;
      case 'quad': return 4;
      case 'sharing': return 'Any';
      default: return 2;
    }
  };

  // Calculate number of rooms needed
  const calculateRoomsNeeded = () => {
    if (bookingConfig.roomType === 'sharing') {
      return bookingConfig.roomQuantity; // User controls quantity for sharing
    }
    
    const roomCapacity = getRoomCapacity(bookingConfig.roomType);
    if (roomCapacity === 'Any') return 1;
    
    // Only adults and children need room space (infants don't)
    const roomOccupants = bookingConfig.adults + bookingConfig.children;
    return Math.ceil(roomOccupants / roomCapacity);
  };

  // Update room quantity when passengers change
  const updateRoomQuantity = () => {
    const roomsNeeded = calculateRoomsNeeded();
    setBookingConfig(prev => ({
      ...prev,
      roomQuantity: roomsNeeded
    }));
  };

  // Validate room capacity for specific room types
  const validateRoomCapacity = (adults, children, roomType) => {
    if (roomType === 'sharing') return { isValid: true, message: '' };
    
    const roomCapacity = getRoomCapacity(roomType);
    if (roomCapacity === 'Any') return { isValid: true, message: '' };
    
    const totalOccupants = adults + children;
    const remainder = totalOccupants % roomCapacity;
    
    if (remainder === 0) {
      return { isValid: true, message: '' };
    } else {
      const nextValidCount = Math.ceil(totalOccupants / roomCapacity) * roomCapacity;
      const prevValidCount = Math.floor(totalOccupants / roomCapacity) * roomCapacity;
      
      return {
        isValid: false,
        message: `${roomType.toUpperCase()} room requires multiples of ${roomCapacity} people. Please adjust to ${prevValidCount} or ${nextValidCount} people.`
      };
    }
  };

  // Get room capacity requirements message
  const getRoomCapacityMessage = (roomType) => {
    const capacity = getRoomCapacity(roomType);
    if (capacity === 'Any') return 'Flexible occupancy - any number of people';
    return `${roomType.toUpperCase()} room: ${capacity} people per room (must be multiples of ${capacity})`;
  };

  // Update room quantity when booking config changes
  useEffect(() => {
    updateRoomQuantity();
  }, [bookingConfig.roomType, bookingConfig.adults, bookingConfig.children]);

  // Initialize booking based on selection type
  useEffect(() => {
    if (show && selectedPackage) {
      if (existingBooking) {
        // Editing existing booking - populate with existing data
        setBookingConfig({
          roomType: existingBooking.room_type,
          roomQuantity: existingBooking.room_quantity,
          adults: existingBooking.number_of_adults,
          children: existingBooking.number_of_children,
          infants: existingBooking.number_of_infants
        });
        
        // Find the pricing for the existing room type
        let pricingData = selectedPackage.prices;
        if (!pricingData && selectedPackage.umrah_package && selectedPackage.umrah_package.prices) {
          pricingData = selectedPackage.umrah_package.prices;
        }
        
        const existingPricing = pricingData?.find(p => p.room_type === existingBooking.room_type);
        
        if (existingPricing) {
          setSelectedPricing(existingPricing);
        } else {
          // Fallback: use first available pricing
          const fallbackPricing = pricingData?.[0];
          setSelectedPricing(fallbackPricing);
        }
        
        // Load existing agent if booking has agent_id
        if (existingBooking.agent_id) {
          setBookForAgent(true);
          setSelectedAgentId(existingBooking.agent_id);
          setAgentSelected(true);
          // Set agent name if available in booking data
          if (existingBooking.agent) {
            setAgentSearchTerm(existingBooking.agent.name || existingBooking.agent.agency_name || '');
          } else {
            // If agent data is not loaded, we can still set the agent_id and let user re-select if needed
            setAgentSearchTerm('');
          }
        }
        
        // Don't set passengers here - let the passenger generation effect handle it
        
        setStep(2); // Go directly to passenger forms for editing
      } else if (selectedPackage.bookingType === 'room_specific') {
        // Direct room type selection - go straight to passenger details with room capacity-based initial values
        const roomCapacity = getRoomCapacity(selectedPackage.selectedRoomType);
        const initialAdults = roomCapacity === 'Any' ? 1 : roomCapacity;
        
        setBookingConfig({
          roomType: selectedPackage.selectedRoomType,
          roomQuantity: 1,
          adults: initialAdults, // Set based on room capacity
          children: 0,
          infants: 0
        });
        setSelectedPricing(selectedPackage.selectedPricing);
        setStep(2); // Go directly to passenger forms
      } else {
        // General booking - let user choose room type first
        setBookingConfig({
          roomType: '',
          roomQuantity: 1,
          adults: 1,
          children: 0,
          infants: 0
        });
        setSelectedPricing(null);
        setStep(1); // Start with room selection
      }
      
      if (!existingBooking) {
        setPassengers([]);
      }
    }
  }, [show, selectedPackage, existingBooking]);

  // Generate passenger forms when config changes
  useEffect(() => {
    if (step === 2) {
      const totalPassengers = bookingConfig.adults + bookingConfig.children + bookingConfig.infants;
      if (totalPassengers > 0) {
        // For new bookings or when passenger count changes
        if (!existingBooking || passengers.length !== totalPassengers) {
          const newPassengers = [];
          
          // Add adults
          for (let i = 0; i < bookingConfig.adults; i++) {
            newPassengers.push({
              type: 'adult',
              title: '', firstname: '', lastname: '', dob: '', 
              passport_no: '', passport_expiry_date: '', 
              passport_img: null, visa_copy: null, remarks: ''
            });
          }
          
          // Add children
          for (let i = 0; i < bookingConfig.children; i++) {
            newPassengers.push({
              type: 'child',
              title: '', firstname: '', lastname: '', dob: '', 
              passport_no: '', passport_expiry_date: '', 
              passport_img: null, visa_copy: null, remarks: ''
            });
          }
          
          // Add infants
          for (let i = 0; i < bookingConfig.infants; i++) {
            newPassengers.push({
              type: 'infant',
              title: '', firstname: '', lastname: '', dob: '', 
              passport_no: '', passport_expiry_date: '', 
              passport_img: null, visa_copy: null, remarks: ''
            });
          }
          
          setPassengers(newPassengers);
        }
      }
    }
  }, [step, bookingConfig]);

  // Handle existing passenger data for editing
  useEffect(() => {
    if (existingBooking && existingBooking.passengers && existingBooking.passengers.length > 0 && passengers.length > 0) {
      const formattedPassengers = existingBooking.passengers.map(p => {
        // Parse full_name to extract title, firstname, lastname
        const fullName = p.full_name || '';
        const nameParts = fullName.trim().split(' ');
        const title = nameParts[0] || '';
        const firstname = nameParts[1] || '';
        const lastname = nameParts.slice(2).join(' ') || '';
        
        
        return {
          type: p.passenger_type || 'adult',
          title: title,
          firstname: firstname,
          lastname: lastname,
          dob: p.date_of_birth ? new Date(p.date_of_birth).toISOString().split('T')[0] : '',
          passport_no: p.passport_number || '',
          passport_expiry_date: p.passport_expiry_date ? (() => {
            try {
              return new Date(p.passport_expiry_date).toISOString().split('T')[0];
            } catch (e) {
              console.log('Passport expiry date error:', p.passport_expiry_date, e);
              return p.passport_expiry_date; // Return as is if date parsing fails
            }
          })() : '',
          gender: p.gender || 'male',
          nationality: p.nationality || 'Pakistani',
          passport_img: p.passport_img,
          cnic_picture: p.cnic_picture,
          picture: p.picture,
          remarks: p.remarks || ''
        };
      });
      
      // Merge existing data with new passenger structure
      const mergedPassengers = passengers.map((passenger, index) => {
        const existingPassenger = formattedPassengers[index];
        if (existingPassenger) {
          return {
            ...passenger,
            ...existingPassenger
          };
        }
        return passenger;
      });
      
      setPassengers(mergedPassengers);
      
      // Extract and store existing file names for display
      const fileNames = {};
      formattedPassengers.forEach((passenger, index) => {
        fileNames[`passport_${index}`] = passenger.passport_img ? extractFileName(passenger.passport_img) : null;
        fileNames[`cnic_${index}`] = passenger.cnic_picture ? extractFileName(passenger.cnic_picture) : null;
        fileNames[`picture_${index}`] = passenger.picture ? extractFileName(passenger.picture) : null;
      });
      setExistingFileNames(fileNames);
    }
  }, [existingBooking, passengers.length]);

  // Calculate pricing
  const calculateTotal = () => {
    if (!selectedPricing) return 0;
    const adultCost = (selectedPricing.price_adult || 0) * bookingConfig.adults;
    const childCost = (selectedPricing.price_child || 0) * bookingConfig.children;
    const infantCost = (selectedPricing.price_infant || 0) * bookingConfig.infants;
    return adultCost + childCost + infantCost;
  };

  // Calculate discount
  const calculateDiscount = () => {
    if (!selectedPackage || !selectedPackage.discount_type) return { total: 0, adult: 0, child: 0, infant: 0 };
    
    if (!selectedPricing) return { total: 0, adult: 0, child: 0, infant: 0 };
    
    let adultDiscount = 0;
    let childDiscount = 0;
    let infantDiscount = 0;
    
    if (selectedPackage.discount_type === 'percentage') {
      if (selectedPackage.discount_adult) {
        adultDiscount = (selectedPricing.price_adult * selectedPackage.discount_adult / 100) * bookingConfig.adults;
      }
      if (selectedPackage.discount_child) {
        childDiscount = (selectedPricing.price_child * selectedPackage.discount_child / 100) * bookingConfig.children;
      }
      if (selectedPackage.discount_infant) {
        infantDiscount = (selectedPricing.price_infant * selectedPackage.discount_infant / 100) * bookingConfig.infants;
      }
    } else if (selectedPackage.discount_type === 'fixed') {
      if (selectedPackage.discount_adult) {
        adultDiscount = Math.min(selectedPackage.discount_adult, selectedPricing.price_adult) * bookingConfig.adults;
      }
      if (selectedPackage.discount_child) {
        childDiscount = Math.min(selectedPackage.discount_child, selectedPricing.price_child) * bookingConfig.children;
      }
      if (selectedPackage.discount_infant) {
        infantDiscount = Math.min(selectedPackage.discount_infant, selectedPricing.price_infant) * bookingConfig.infants;
      }
    }
    
    return {
      total: adultDiscount + childDiscount + infantDiscount,
      adult: adultDiscount,
      child: childDiscount,
      infant: infantDiscount
    };
  };

  // Calculate final amount after discount
  const calculateFinalAmount = () => {
    const totalAmount = calculateTotal();
    const discountAmount = calculateDiscount().total;
    return totalAmount - discountAmount;
  };

  const updatePassenger = (idx, key, value) => {
    setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
    
    // Clear field error when user starts typing/selecting
    if (fieldErrors[`passenger_${idx}`] && fieldErrors[`passenger_${idx}`][key]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[`passenger_${idx}`]) {
          delete newErrors[`passenger_${idx}`][key];
          if (Object.keys(newErrors[`passenger_${idx}`]).length === 0) {
            delete newErrors[`passenger_${idx}`];
          }
        }
        return newErrors;
      });
    }
    
    // Real-time age validation for DOB changes
    if (key === 'dob' && value) {
      const passenger = passengers[idx];
      if (passenger && (passenger.type === 'infant' || passenger.type === 'child')) {
        const ageValidation = validateAge(value, passenger.type);
        if (!ageValidation.isValid) {
          // Show toast warning
          toast.error(ageValidation.message, {
            duration: 6000,
            style: {
              maxWidth: '500px',
              whiteSpace: 'pre-line'
            }
          });
        }
      }
    }
    
    // Clear existing file name when new file is selected
    if (key === 'passport_img' && value) {
      setExistingFileNames(prev => ({
        ...prev,
        [`passport_${idx}`]: null
      }));
    } else if (key === 'cnic_picture' && value) {
      setExistingFileNames(prev => ({
        ...prev,
        [`cnic_${idx}`]: null
      }));
    } else if (key === 'picture' && value) {
      setExistingFileNames(prev => ({
        ...prev,
        [`picture_${idx}`]: null
      }));
    }
  };

  // Extract file name from path
  const extractFileName = (filePath) => {
    if (!filePath) return null;
    return filePath.split('/').pop();
  };

  // Get error message for a specific field
  const getFieldError = (passengerIndex, fieldName) => {
    const passengerErrors = fieldErrors[`passenger_${passengerIndex}`];
    return passengerErrors ? passengerErrors[fieldName] : null;
  };

  // Calculate return date based on package details
  const calculateReturnDate = () => {
    if (!selectedPackage) return null;
    
    // Get departure date from the first departure flight
    const departureFlight = selectedPackage.departure_flights?.[0];
    if (!departureFlight?.departure_date) return null;
    
    const departureDate = new Date(departureFlight.departure_date);
    
    // Calculate nights from package data
    let nights = 0;
    if (selectedPackage.departure_flights && selectedPackage.return_flights) {
      const returnFlight = selectedPackage.return_flights[0];
      if (returnFlight?.arrival_date) {
        const returnDate = new Date(returnFlight.arrival_date);
        const diffTime = Math.abs(returnDate - departureDate);
        nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
    }
    
    // If we can't calculate from flights, try to get nights from package
    if (nights === 0 && selectedPackage.nights) {
      nights = selectedPackage.nights;
    }
    
    // Add nights to departure date to get return date
    const returnDate = new Date(departureDate);
    returnDate.setDate(returnDate.getDate() + nights);
    
    return returnDate;
  };

  // Validate age based on passenger type and return date
  const validateAge = (dob, passengerType) => {
    if (!dob) return { isValid: true, message: '' };
    
    const returnDate = calculateReturnDate();
    if (!returnDate) return { isValid: true, message: '' }; // Skip validation if we can't calculate return date
    
    const birthDate = new Date(dob);
    const ageAtReturn = returnDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = returnDate.getMonth() - birthDate.getMonth();
    
    // Calculate exact age in years and months
    let exactAgeYears = ageAtReturn;
    if (monthDiff < 0 || (monthDiff === 0 && returnDate.getDate() < birthDate.getDate())) {
      exactAgeYears--;
    }
    
    const exactAgeMonths = (returnDate.getFullYear() - birthDate.getFullYear()) * 12 + 
                          (returnDate.getMonth() - birthDate.getMonth());
    
    if (passengerType === 'infant') {
      // Infant should be less than 2 years (23 months 26 days) at return
      const maxAgeMonths = 23;
      const maxAgeDays = 26;
      
      if (exactAgeYears >= 2 || (exactAgeYears === 1 && exactAgeMonths >= 23 && returnDate.getDate() >= birthDate.getDate() + 26)) {
        return {
          isValid: false,
          message: `Infant must be less than 2 years old at return date (${returnDate.toLocaleDateString()}). Current age would be ${exactAgeYears} years ${exactAgeMonths % 12} months.`
        };
      }
    } else if (passengerType === 'child') {
      // Child should be less than 12 years (11 years 11 months 26 days) at return
      const maxAgeYears = 11;
      const maxAgeMonths = 11;
      const maxAgeDays = 26;
      
      if (exactAgeYears >= 12 || (exactAgeYears === 11 && exactAgeMonths >= 11 && returnDate.getDate() >= birthDate.getDate() + 26)) {
        return {
          isValid: false,
          message: `Child must be less than 12 years old at return date (${returnDate.toLocaleDateString()}). Current age would be ${exactAgeYears} years ${exactAgeMonths % 12} months.`
        };
      }
    }
    
    return { isValid: true, message: '' };
  };

  const handleRoomSelection = (roomType, pricing) => {
    const capacity = getRoomCapacity(roomType);
    const requiredSeats = capacity === 'Any' ? 1 : capacity;
    
    // Check if there are enough seats available
    if (selectedPackage.available_seats < requiredSeats) {
      toast.error(`Only ${selectedPackage.available_seats} seat(s) available. Cannot select ${roomType.toUpperCase()} room (requires ${requiredSeats} people).`);
      return;
    }
    
    setBookingConfig(prev => ({
      ...prev,
      roomType,
      adults: requiredSeats,
      children: 0,
      infants: 0
    }));
    setSelectedPricing(pricing);
    setStep(2);
  };

  // Handle passenger count changes - allow any input but validate for form enabling
  const handlePassengerCountChange = (type, value) => {
    const newAdults = type === 'adults' ? parseInt(value) || 0 : bookingConfig.adults;
    const newChildren = type === 'children' ? parseInt(value) || 0 : bookingConfig.children;
    const newInfants = type === 'infants' ? parseInt(value) || 0 : bookingConfig.infants;
    
    // Validate: Children and infants require at least one adult
    if ((newChildren > 0 || newInfants > 0) && newAdults === 0) {
      toast.error('At least one adult is required when booking children or infants');
      return;
    }
    
    // Check total passengers against available seats
    const totalPassengers = newAdults + newChildren;
    if (totalPassengers > (selectedPackage?.available_seats || 0)) {
      toast.error(`Only ${selectedPackage?.available_seats || 0} seats available`);
      return;
    }
    
    // Allow any input - validation will be checked for form enabling
    setBookingConfig(prev => ({
      ...prev,
      [type]: parseInt(value) || 0
    }));
  };

  // Check if passenger details form should be enabled
  const isPassengerFormEnabled = () => {
    if (bookingConfig.roomType === 'sharing') {
      // For sharing rooms, check if adults are present when children/infants are present
      if ((bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0) {
        return false;
      }
      return true;
    }
    if (!bookingConfig.roomType) return false;
    
    // Validate: Children and infants require at least one adult
    if ((bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0) {
      return false;
    }
    
    const validation = validateRoomCapacity(bookingConfig.adults, bookingConfig.children, bookingConfig.roomType);
    return validation.isValid;
  };

  // Handle agent search
  const handleAgentSearch = async (e) => {
    const searchValue = e.target.value;
    setAgentSearchTerm(searchValue);
    
    if (!searchValue) {
      setAgentSearchResults([]);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/searchagent`,
        { searchquery: searchValue },
        {
          headers: {
            'Authorization': `Bearer ${getBearerToken()}`
          }
        }
      );
      setAgentSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Agent search error:', error);
      setAgentSearchResults([]);
    }
  };

  // Handle agent selection
  const handleAgentSelection = (agent) => {
    setAgentSearchResults([]);
    setAgentSelected(true);
    setAgentSearchTerm(agent.name);
    setSelectedAgentId(agent.id);
  };

  // Handle checkbox change for "Book For Agent"
  const handleBookForAgentChange = (e) => {
    const checked = e.target.checked;
    setBookForAgent(checked);
    
    if (!checked) {
      // Reset agent selection when unchecked
      setAgentSearchTerm('');
      setAgentSelected(false);
      setSelectedAgentId(null);
      setAgentSearchResults([]);
    } else {
      // When checked, reset to default state
      setAgentSearchTerm('');
      setAgentSelected(false);
      setSelectedAgentId(null);
    }
  };

  // Validate all required passenger fields and return field-level errors
  const validatePassengerData = () => {
    const fieldErrors = {};
    let hasErrors = false;
    
    // Validate: Children and infants require at least one adult
    if ((bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0) {
      return { 
        isValid: false, 
        fieldErrors: { 
          general: 'At least one adult is required when booking children or infants. Please add at least one adult passenger.' 
        } 
      };
    }
    
    // Check if we have passengers
    if (!passengers || passengers.length === 0) {
      return { isValid: false, fieldErrors: { general: 'No passengers added. Please add at least one passenger.' } };
    }
    
    // Validate each passenger
    passengers.forEach((passenger, index) => {
      const passengerErrors = {};
      
      // Required text fields
      if (!passenger.title || passenger.title.trim() === '') {
        passengerErrors.title = 'Title is required';
        hasErrors = true;
      }
      
      if (!passenger.firstname || passenger.firstname.trim() === '') {
        passengerErrors.firstname = 'First name is required';
        hasErrors = true;
      }
      
      if (!passenger.lastname || passenger.lastname.trim() === '') {
        passengerErrors.lastname = 'Last name is required';
        hasErrors = true;
      }
      
      if (!passenger.dob || passenger.dob.trim() === '') {
        passengerErrors.dob = 'Date of birth is required';
        hasErrors = true;
      } else {
        // Validate date of birth is in the past
        const birthDate = new Date(passenger.dob);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (birthDate >= today) {
          passengerErrors.dob = 'Date of birth must be in the past';
          hasErrors = true;
        } else {
          // Validate age limits for infants and children
          const ageValidation = validateAge(passenger.dob, passenger.type);
          if (!ageValidation.isValid) {
            passengerErrors.dob = ageValidation.message;
            hasErrors = true;
          }
        }
      }
      
      if (!passenger.passport_no || passenger.passport_no.trim() === '') {
        passengerErrors.passport_no = 'Passport number is required';
        hasErrors = true;
      }
      
      if (!passenger.passport_expiry_date || passenger.passport_expiry_date.trim() === '') {
        passengerErrors.passport_expiry_date = 'Passport expiry date is required';
        hasErrors = true;
      } else {
        // Validate passport expiry date is in the future
        const expiryDate = new Date(passenger.passport_expiry_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (expiryDate <= today) {
          passengerErrors.passport_expiry_date = 'Passport expiry date must be in the future';
          hasErrors = true;
        }
      }
      
      // Required file uploads
      if (!passenger.passport_img || (!(passenger.passport_img instanceof File) && !passenger.passport_img)) {
        passengerErrors.passport_img = 'Passport photo/PDF is required';
        hasErrors = true;
      }
      
      // Store errors for this passenger
      if (Object.keys(passengerErrors).length > 0) {
        fieldErrors[`passenger_${index}`] = passengerErrors;
      }
    });
    
    return {
      isValid: !hasErrors,
      fieldErrors: fieldErrors
    };
  };

  const handleSubmit = async () => {
    // Validate "Book For Agent" if checked
    if (bookForAgent && !selectedAgentId) {
      toast.error('Please select an agent when "Book For Agent" is checked');
      return;
    }
    
    // Validate passenger data first
    const validation = validatePassengerData();
    if (!validation.isValid) {
      // Set field errors to display under each input
      setFieldErrors(validation.fieldErrors);
      
      // Show a general error message
      if (validation.fieldErrors.general) {
        toast.error(validation.fieldErrors.general);
      } else {
        toast.error('Please fill in all required fields marked with *');
      }
      
      return;
    }
    
    // Clear any existing field errors if validation passes
    setFieldErrors({});

    const tid = toast.loading(existingBooking ? 'Updating booking...' : 'Processing booking...');
    setLoading(true);
    
    try {
      const userId = token?.id || token?.user_id || token?.user?.id;
      
      if (!userId) {
        toast.error('Please login to continue booking');
        return;
      }

      // Check seat availability
      const totalPassengers = bookingConfig.adults + bookingConfig.children;
      if (selectedPackage.available_seats < totalPassengers) {
        toast.error(`Only ${selectedPackage.available_seats} seats available. Please reduce the number of passengers.`);
        setLoading(false);
        toast.dismiss(tid);
        return;
      }

      // Validate room capacity for specific room types
      if (bookingConfig.roomType && bookingConfig.roomType !== 'sharing') {
        const validation = validateRoomCapacity(bookingConfig.adults, bookingConfig.children, bookingConfig.roomType);
        if (!validation.isValid) {
          toast.error(validation.message);
          setLoading(false);
          toast.dismiss(tid);
          return;
        }
      }

      // Create FormData instead of JSON payload
      const formData = new FormData();
      
      // Append booking-level data
      formData.append('user_id', userId);
      formData.append('umrah_package_id', selectedPackage?.id);
      formData.append('room_type', bookingConfig.roomType);
      formData.append('room_quantity', calculateRoomsNeeded());
      formData.append('number_of_adults', bookingConfig.adults);
      formData.append('number_of_children', bookingConfig.children);
      formData.append('number_of_infants', bookingConfig.infants);
      formData.append('total_amount', calculateFinalAmount());
      
      // Append agent_id if "Book For Agent" is enabled and agent is selected
      if (bookForAgent && selectedAgentId) {
        formData.append('agent_id', selectedAgentId);
      } else if (bookForAgent && !selectedAgentId) {
        // If checkbox is checked but no agent selected, send 0
        formData.append('agent_id', 0);
      } else {
        // If checkbox is not checked, don't send agent_id (will be null)
        formData.append('agent_id', 0);
      }
      
      // Append passenger data as arrays
      passengers.forEach((p, index) => {
        formData.append('full_name[]', `${p.title} ${p.firstname} ${p.lastname}`.trim());
        formData.append('passport_number[]', p.passport_no || '');
        formData.append('passport_expiry_date[]', p.passport_expiry_date || '');
        formData.append('date_of_birth[]', p.dob || '');
        formData.append('passenger_type[]', p.type);
        formData.append('gender[]', p.gender || 'male');
        formData.append('nationality[]', p.nationality || 'Pakistani');
        formData.append('remarks[]', p.remarks || '');
        
        // Append files with indexed keys
        if (p.passport_img && p.passport_img instanceof File) {
          formData.append(`passport_img[${index}]`, p.passport_img);
        }
        if (p.cnic_picture && p.cnic_picture instanceof File) {
          formData.append(`cnic_picture[${index}]`, p.cnic_picture);
        }
        if (p.picture && p.picture instanceof File) {
          formData.append(`picture[${index}]`, p.picture);
        }
      });
      
      let res;
      if (existingBooking) {
        formData.append('booking_id', existingBooking.id);
        res = await axios.post(`${API_BASE_URL}/umrah-bookings/update`, formData, {
          headers: { Authorization: `Bearer ${getBearerToken()}` },
        });
      } else {
        res = await axios.post(`${API_BASE_URL}/umrah-package/book`, formData, {
          headers: { Authorization: `Bearer ${getBearerToken()}` },
        });
      }
      
      if (res?.data?.status) {
        toast.success(existingBooking ? '🎉 Booking updated successfully!' : '🎉 Umrah booking confirmed successfully!');
        handleClose();
      } else {
        toast.error(res?.data?.message || 'Booking failed. Please try again.');
      }
    } catch (e) {
      console.error('Booking error:', e);
      toast.error(e?.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
      toast.dismiss(tid);
    }
  };

  return (
    <Modal show={!!show} size="xl" onHide={handleClose} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="border-0 pb-2">
        <Modal.Title className="d-flex align-items-center">
          <i className="fas fa-kaaba me-2 text-success"></i>
          {existingBooking ? 'Edit Booking' : 'Book Umrah'} - {selectedPackage?.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-2">
        <Form className={'AllForm'} method='post'>
        {/* Seat Availability Info */}
        <div className="alert alert-info mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <i className="fas fa-users me-2"></i>
              <strong>Seats Available:</strong> {selectedPackage?.available_seats || 0} / {selectedPackage?.total_seats || 0}
            </div>
            {selectedPackage?.available_seats === 0 && (
              <Badge bg="danger">Fully Booked</Badge>
            )}
          </div>
        </div>

        {/* Book For Agent Section - Only show for admins */}
        {token?.role === 'admin' && (
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <Form.Check
                type="checkbox"
                label="Book for Agent"
                className="me-4"
                id="bookForAgentCheckbox"
                checked={bookForAgent}
                onChange={handleBookForAgentChange}
              />
              {bookForAgent && (
                <div className="w-100">
                  <div className="text-end mb-2">
                    <Link to="#" className="text-success text-decoration-none" onClick={(e) => { e.preventDefault(); setAddAgent(true); }}>
                      <i className="fas fa-plus me-1"></i>Add new Agent
                    </Link>
                  </div>
                  <InputGroup className="form-group">
                    <Form.Label>Agent Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Agent by name"
                      onChange={handleAgentSearch}
                      readOnly={agentSelected}
                      value={agentSearchTerm}
                      required={bookForAgent}
                    />
                    {agentSelected && (
                      <Button
                        variant="outline-secondary"
                        className="fontweight700"
                        onClick={() => {
                          setAgentSearchTerm('');
                          setAgentSelected(false);
                          setSelectedAgentId(null);
                        }}
                      >
                        Select new Agent
                      </Button>
                    )}
                  </InputGroup>
                  {agentSearchTerm && !agentSelected && (
                    <div className="searchdropdow mt-1">
                      {loading ? (
                        <div className="mt-3 w-100 text-center">
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        </div>
                      ) : (
                        <>
                          {agentSearchResults.length > 0
                            ? agentSearchResults.map((agent) => (
                                <NavItem
                                  className=""
                                  key={agent.id}
                                  onClick={() => handleAgentSelection(agent)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <p className="p-3 mb-0">{agent.name}</p>
                                </NavItem>
                              ))
                            : !agentSelected && <div className="p-3 text-muted">No Agent Found</div>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Room Type Selection (for general booking) */}
        {step === 1 && (
          <div>
            <div className="text-center mb-4">
              <h5 className="fontweight600 mb-2">Choose Your Room Type</h5>
              <p className="text-muted mb-0">Select the room configuration that works best for your group</p>
            </div>
            
            <Row className="justify-content-center">
              {selectedPackage?.prices?.filter(p => 
                (p.price_adult && p.price_adult > 0) || 
                (p.price_child && p.price_child > 0) || 
                (p.price_infant && p.price_infant > 0)
              ).reverse().map((price, idx) => {
                const capacity = getRoomCapacity(price.room_type);
                const requiredSeats = capacity === 'Any' ? 1 : capacity;
                const isAvailable = selectedPackage.available_seats >= requiredSeats;
                
                return (
                <Col md={6} lg={3} key={idx} className="mb-3">
                  <Card 
                    className={`h-100 border-2 text-center ${!isAvailable ? 'opacity-50' : ''}`}
                    style={{ 
                      cursor: isAvailable ? 'pointer' : 'not-allowed', 
                      transition: 'all 0.2s ease', 
                      borderRadius: '10px',
                      backgroundColor: !isAvailable ? '#f8f9fa' : 'white',
                      borderColor: isAvailable ? '#28a745' : '#dee2e6'
                    }}
                    onClick={() => isAvailable ? handleRoomSelection(price.room_type, price) : null}
                  >
                    <Card.Body className="p-3">
                      <i className={`fas fa-bed fa-2x mb-2 ${isAvailable ? 'text-success' : 'text-muted'}`} style={{ color: isAvailable ? '#28a745' : undefined }}></i>
                      <h6 className={`fontweight700 mb-1 ${!isAvailable ? 'text-muted' : ''}`}>{(price.room_type === "sharing" ? "quint/sharing" : price.room_type).toUpperCase()}</h6>
                      <p className="text-muted mb-2 fontsize12">
                        {getRoomCapacity(price.room_type) === 'Any' ? 'Any number of people' : `${getRoomCapacity(price.room_type)} people per room`}
                      </p>
                      {getRoomCapacity(price.room_type) !== 'Any' && (
                        <div className="mb-2">
                          <Badge bg="info" className="fontsize10 px-2 py-1">
                            <i className="fas fa-calculator me-1"></i>
                            Auto-calculated rooms
                          </Badge>
                          <div className="mt-1">
                            <small className="text-warning fontsize10">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Must be multiples of {getRoomCapacity(price.room_type)}
                            </small>
                          </div>
                        </div>
                      )}
                      {!isAvailable && (
                        <div className="mb-2">
                          <Badge bg="danger" className="fontsize11">Not Available</Badge>
                          <p className="text-muted fontsize11 mt-1 mb-0">
                            Requires {requiredSeats} people, only {selectedPackage.available_seats} seat(s) left
                          </p>
                        </div>
                      )}
                      <div className="pricing-info">
                        <h5 className={`fontweight700 mb-1 ${isAvailable ? 'text-success' : 'text-muted'}`} style={{ color: isAvailable ? '#28a745' : undefined }}>
                          PKR {Number(price.price_adult).toLocaleString()}
                        </h5>
                        <small className="text-muted d-block">per adult</small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* Step 2: Passenger Details */}
        {step === 2 && (
          <div>
            {/* Header with room info and passenger count controls */}
            <div className="bg-light p-3 rounded mb-4">
              <Row className="align-items-center">
                <Col md={6}>
                  <h6 className="mb-1 fontweight600">
                    <i className="fas fa-bed me-2 text-primary"></i>
                    {(bookingConfig.roomType === "sharing" ? "quint/sharing" : bookingConfig.roomType).toUpperCase()} Room Selected
                  </h6>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <small className="text-muted">
                      {getRoomCapacity(bookingConfig.roomType) === 'Any' ? 'Unlimited capacity' : `${getRoomCapacity(bookingConfig.roomType)} people per room`}
                    </small>
                    {bookingConfig.roomType !== 'sharing' && (
                      <>
                        <Badge bg="info" className="px-2 py-1">
                          <i className="fas fa-home me-1"></i>
                          {calculateRoomsNeeded()} {calculateRoomsNeeded() === 1 ? 'Room' : 'Rooms'} Needed
                        </Badge>
                        <small className="text-info d-flex align-items-center">
                          <i className="fas fa-info-circle me-1"></i>
                          <span className="d-none d-sm-inline">Infants don't need room space</span>
                          <span className="d-inline d-sm-none">Infants: no room space</span>
                        </small>
                      </>
                    )}
                  </div>
                  
                  {/* Room Capacity Validation Message - Only show when invalid */}
                  {bookingConfig.roomType && bookingConfig.roomType !== 'sharing' && !validateRoomCapacity(bookingConfig.adults, bookingConfig.children, bookingConfig.roomType).isValid && (
                    <div className="mt-3">
                      <div className="bg-warning bg-opacity-10 border border-warning rounded p-3" style={{ fontSize: '13px' }}>
                        <div className="d-flex align-items-start">
                          <i className="fas fa-exclamation-triangle text-warning me-2 mt-1" style={{ fontSize: '14px' }}></i>
                          <div className="flex-grow-1">
                            <div className="fontweight600 text-dark mb-2">
                              {getRoomCapacityMessage(bookingConfig.roomType)}
                            </div>
                            <div>
                              <div className="text-warning mb-2">
                                <i className="fas fa-exclamation-circle me-1"></i>
                                {validateRoomCapacity(bookingConfig.adults, bookingConfig.children, bookingConfig.roomType).message}
                              </div>
                              <div className="bg-white border rounded p-2">
                                <i className="fas fa-lock text-muted me-1"></i>
                                <small className="text-muted">
                                  Passenger details form will be enabled once room capacity is valid
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <Row className="g-2">
                    <Col xs={4}>
                        <Form.Group className="form-group">
                          <Form.Label>Adults</Form.Label>
                          <Form.Control 
                            type="number" 
                            min="0" 
                            max={selectedPackage?.available_seats || 999}
                            value={bookingConfig.adults}
                            onChange={(e) => handlePassengerCountChange('adults', e.target.value)}
                          />
                        <small className="text-success">PKR {(selectedPricing?.price_adult || 0).toLocaleString()}</small>
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Form.Group className="form-group">
                        <Form.Label>Children</Form.Label>
                        <Form.Control 
                          type="number" 
                          min="0" 
                          max={selectedPackage?.available_seats || 999}
                          value={bookingConfig.children}
                          onChange={(e) => handlePassengerCountChange('children', e.target.value)}
                        />
                        <small className="text-warning">PKR {(selectedPricing?.price_child || 0).toLocaleString()}</small>
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Form.Group className="form-group">
                        <Form.Label>Infants</Form.Label>
                        <Form.Control 
                          type="number" 
                          min="0" 
                          value={bookingConfig.infants}
                          onChange={(e) => handlePassengerCountChange('infants', e.target.value)}
                        />
                        <small className="text-info">PKR {(selectedPricing?.price_infant || 0).toLocaleString()}</small>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Warning: Children/Infants require adult */}
                  {(bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0 && (
                    <div className="mt-3">
                      <div className="bg-danger bg-opacity-10 border border-danger rounded p-2" style={{ fontSize: '12px' }}>
                        <div className="d-flex align-items-start">
                          <i className="fas fa-exclamation-triangle text-danger me-2 mt-1" style={{ fontSize: '12px' }}></i>
                          <div className="text-danger">
                            <strong>At least one adult is required</strong> when booking children or infants. Please add at least one adult passenger.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
              <hr className="my-2" />
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="fontweight600">Total Passengers: {bookingConfig.adults + bookingConfig.children + bookingConfig.infants}</span>
                  <br />
                  <small className="text-muted">
                    {(bookingConfig.roomType === 'sharing' ? 'quint/sharing' : bookingConfig.roomType) === 'quint/sharing' ? 'Bed' : 'Room'} Occupants: {bookingConfig.adults + bookingConfig.children} 
                    {bookingConfig.infants > 0 && (
                      <span> • Infants: {bookingConfig.infants} (no {(bookingConfig.roomType === 'sharing' ? 'quint/sharing' : bookingConfig.roomType) === 'quint/sharing' ? 'bed' : 'room'} space)</span>
                    )}
                  </small>
                </div>
              </div>
              
              {/* Detailed Pricing Breakdown */}
              <div className="mt-4 p-3 bg-white border rounded">
                <h6 className="fontweight600 mb-3 text-primary">
                  <i className="fas fa-calculator me-2"></i>
                  Detailed Price Breakdown
                </h6>
                
                <div className="row">
                  <div className="col-md-8">
                    <div className="pricing-breakdown">
                      {/* Adults Pricing */}
                      {bookingConfig.adults > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-user me-2 text-primary"></i>
                            <span className="fontweight600">Adults ({bookingConfig.adults} × PKR {(selectedPricing?.price_adult || 0).toLocaleString()})</span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600">PKR {((selectedPricing?.price_adult || 0) * bookingConfig.adults).toLocaleString()}</span>
                            <br />
                            <small className="text-muted">PKR {(selectedPricing?.price_adult || 0).toLocaleString()} per adult</small>
                          </div>
                        </div>
                      )}
                      
                      {/* Children Pricing */}
                      {bookingConfig.children > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-child me-2 text-warning"></i>
                            <span className="fontweight600">Children ({bookingConfig.children} × PKR {(selectedPricing?.price_child || 0).toLocaleString()})</span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600">PKR {((selectedPricing?.price_child || 0) * bookingConfig.children).toLocaleString()}</span>
                            <br />
                            <small className="text-muted">PKR {(selectedPricing?.price_child || 0).toLocaleString()} per child</small>
                          </div>
                        </div>
                      )}
                      
                      {/* Infants Pricing */}
                      {bookingConfig.infants > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-baby me-2 text-info"></i>
                            <span className="fontweight600">Infants ({bookingConfig.infants} × PKR {(selectedPricing?.price_infant || 0).toLocaleString()})</span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600">PKR {((selectedPricing?.price_infant || 0) * bookingConfig.infants).toLocaleString()}</span>
                            <br />
                            <small className="text-muted">PKR {(selectedPricing?.price_infant || 0).toLocaleString()} per infant</small>
                          </div>
                        </div>
                      )}
                      
                      {/* Room Information */}
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-bed me-2 text-secondary"></i>
                          <span className="fontweight600">
                            {calculateRoomsNeeded()} {calculateRoomsNeeded() === 1 ? 'Room' : 'Rooms'} ({(bookingConfig.roomType === "sharing" ? "quint/sharing" : bookingConfig.roomType).toUpperCase()})
                          </span>
                        </div>
                        <div className="text-end">
                          <span className="text-muted">Included in package price</span>
                          <br />
                          <small className="text-muted">
                            {getRoomCapacity(bookingConfig.roomType) === 'Any' ? 'Flexible occupancy' : `${getRoomCapacity(bookingConfig.roomType)} people per room`}
                          </small>
                        </div>
                      </div>
                      
                      {/* Subtotal */}
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-plus-circle me-2 text-dark"></i>
                          <span className="fontweight600">Subtotal (Before Discount)</span>
                        </div>
                        <div className="text-end">
                          <span className="fontweight600">PKR {calculateTotal().toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Adult Discount */}
                      {bookingConfig.adults > 0 && calculateDiscount().adult > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-tag me-2 text-success"></i>
                            <span className="fontweight600 text-success">
                              Adult Discount ({selectedPackage?.discount_adult}{selectedPackage?.discount_type === 'percentage' ? '%' : ' PKR'} × {bookingConfig.adults})
                            </span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600 text-success">-PKR {calculateDiscount().adult.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Child Discount */}
                      {bookingConfig.children > 0 && calculateDiscount().child > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-tag me-2 text-success"></i>
                            <span className="fontweight600 text-success">
                              Child Discount ({selectedPackage?.discount_child}{selectedPackage?.discount_type === 'percentage' ? '%' : ' PKR'} × {bookingConfig.children})
                            </span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600 text-success">-PKR {calculateDiscount().child.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Infant Discount */}
                      {bookingConfig.infants > 0 && calculateDiscount().infant > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-tag me-2 text-success"></i>
                            <span className="fontweight600 text-success">
                              Infant Discount ({selectedPackage?.discount_infant}{selectedPackage?.discount_type === 'percentage' ? '%' : ' PKR'} × {bookingConfig.infants})
                            </span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600 text-success">-PKR {calculateDiscount().infant.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Total Discount */}
                      {calculateDiscount().total > 0 && (
                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-tag me-2 text-success"></i>
                            <span className="fontweight600 text-success">
                              Total Package Discount
                            </span>
                          </div>
                          <div className="text-end">
                            <span className="fontweight600 text-success">-PKR {calculateDiscount().total.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-4">
                    <div className="total-summary bg-light p-3 rounded">
                      <h6 className="fontweight600 mb-3 text-center">Final Amount</h6>
                      <div className="text-center">
                        <div className="display-6 text-success fontweight700">
                          PKR {calculateFinalAmount().toLocaleString()}
                        </div>
                        <small className="text-muted">
                          Total amount
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>

            {/* Passenger Forms */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fontweight600 mb-0">
                  <i className="fas fa-users me-2"></i>
                  Enter Passenger Details
                  {!isPassengerFormEnabled() && (
                    <Badge bg="warning" className="ms-2 px-2 py-1">
                      <i className="fas fa-lock me-1"></i>
                      Form Disabled
                    </Badge>
                  )}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="primary" className="px-3 py-2">
                    <i className="fas fa-bed me-1"></i>
                    {calculateRoomsNeeded()} {calculateRoomsNeeded() === 1 ? 'Room' : 'Rooms'} Needed
                  </Badge>
                  <Badge bg="secondary" className="px-2 py-1">
                    <i className="fas fa-users me-1"></i>
                    {(bookingConfig.roomType === "sharing" ? "quint/sharing" : bookingConfig.roomType).toUpperCase()}
                  </Badge>
                  <small className="text-muted">
                    {getRoomCapacity(bookingConfig.roomType) === 'Any' ? 'Flexible occupancy' : `${getRoomCapacity(bookingConfig.roomType)} people per room`}
                  </small>
                </div>
              </div>
              
              {/* Form Disabled Message */}
              {!isPassengerFormEnabled() && (
                <div className={`border rounded p-3 mb-3 ${(bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0 ? 'bg-danger bg-opacity-10 border-danger' : 'bg-warning bg-opacity-10 border-warning'}`} style={{ fontSize: '13px' }}>
                  <div className="d-flex align-items-start">
                    <i className={`fas fa-exclamation-triangle me-2 mt-1 ${(bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0 ? 'text-danger' : 'text-warning'}`} style={{ fontSize: '14px' }}></i>
                    <div>
                      <div className={`fontweight600 mb-2 ${(bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0 ? 'text-danger' : 'text-warning'}`}>
                        Passenger Details Form is Disabled
                      </div>
                      <div className="text-muted">
                        {(bookingConfig.children > 0 || bookingConfig.infants > 0) && bookingConfig.adults === 0 ? (
                          <>
                            <strong className="text-danger">At least one adult is required</strong> when booking children or infants. 
                            Please add at least one adult passenger to proceed.
                          </>
                        ) : (
                          <>
                            Please adjust the number of adults and children to match the room capacity requirements above. 
                            The form will be enabled once you have a valid room configuration.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              
              <div className={!isPassengerFormEnabled() ? 'opacity-50' : ''} style={{ pointerEvents: !isPassengerFormEnabled() ? 'none' : 'auto' }}>
                {passengers && passengers.length > 0 ? passengers.map((passenger, idx) => (
                  <div key={idx} className="mb-3 border-0 shadow-sm" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px', padding: '20px' }}>
                    <div className="d-flex align-items-center mb-3">
                      <div 
                        className={`rounded-circle d-flex align-items-center justify-content-center me-3`}
                        style={{ 
                          width: '30px', 
                          height: '30px', 
                          backgroundColor: passenger.type === 'adult' ? '#0d6efd' : passenger.type === 'child' ? '#ffc107' : '#17a2b8',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {idx + 1}
                      </div>
                      <h6 className="mb-0 fontweight600">
                        {(passenger.type || 'adult').charAt(0).toUpperCase() + (passenger.type || 'adult').slice(1)} - Passenger {idx + 1}
                        {passenger.type === 'infant' && <small className="text-muted ms-2">(No room seat)</small>}
                      </h6>
                    </div>
                    
                    <Row>
                      <Col md={2}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Title *</Form.Label>
                          <Form.Select 
                            value={passenger.title || ''} 
                            onChange={(e) => updatePassenger(idx, 'title', e.target.value)}
                            required
                            isInvalid={!!getFieldError(idx, 'title')}
                          >
                            <option value="">Select</option>
                            {passenger.type === 'adult' && (
                              <>
                                <option value="Mr">Mr.</option>
                                <option value="Mrs">Mrs.</option>
                                <option value="Ms">Ms.</option>
                              </>
                            )}
                            {passenger.type === 'child' && (
                              <>
                                <option value="Mstr">Mstr.</option>
                                <option value="Miss">Miss.</option>
                              </>
                            )}
                            {passenger.type === 'infant' && (
                              <option value="Inf">Inf.</option>
                            )}
                          </Form.Select>
                          {getFieldError(idx, 'title') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'title')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>First Name *</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="Enter first name" 
                            value={passenger.firstname || ''} 
                            onChange={(e) => updatePassenger(idx, 'firstname', e.target.value)}
                            required
                            isInvalid={!!getFieldError(idx, 'firstname')}
                          />
                          {getFieldError(idx, 'firstname') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'firstname')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Last Name *</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="Enter last name" 
                            value={passenger.lastname || ''} 
                            onChange={(e) => updatePassenger(idx, 'lastname', e.target.value)}
                            required
                            isInvalid={!!getFieldError(idx, 'lastname')}
                          />
                          {getFieldError(idx, 'lastname') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'lastname')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={3}>
                        <Form.Group className="form-group mb-3" controlId="exampleForm.ControlInput1">
                          <Form.Label>Date of Birth *</Form.Label>
                          <Form.Control 
                            type={'date'} 
                            value={passenger.dob || ''} 
                            max={new Date().toISOString().split("T")[0]}
                            onChange={(e) => updatePassenger(idx, 'dob', e.target.value)}
                            required 
                            placeholder="Date"
                            aria-label="Username"
                            aria-describedby="basic-addon1"
                            isInvalid={!!getFieldError(idx, 'dob')}
                          />
                          {getFieldError(idx, 'dob') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'dob')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Passport Number *</Form.Label>
                          <Form.Control 
                            type="text" 
                            placeholder="Enter passport number" 
                            value={passenger.passport_no || ''} 
                            onChange={(e) => updatePassenger(idx, 'passport_no', e.target.value)}
                            required
                            isInvalid={!!getFieldError(idx, 'passport_no')}
                          />
                          {getFieldError(idx, 'passport_no') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'passport_no')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="form-group mb-3" controlId="exampleForm.ControlInput1">
                          <Form.Label>Passport Expiry *</Form.Label>
                          <Form.Control 
                            type={'date'} 
                            value={passenger.passport_expiry_date || ''} 
                            min={new Date().toISOString().split("T")[0]}
                            onChange={(e) => updatePassenger(idx, 'passport_expiry_date', e.target.value)}
                            required 
                            placeholder="Date"
                            aria-label="Username"
                            aria-describedby="basic-addon1"
                            isInvalid={!!getFieldError(idx, 'passport_expiry_date')}
                          />
                          {getFieldError(idx, 'passport_expiry_date') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'passport_expiry_date')}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Gender</Form.Label>
                          <Form.Select 
                            value={passenger.gender || 'male'} 
                            onChange={(e) => updatePassenger(idx, 'gender', e.target.value)}
                          >
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Passport Photo/PDF *</Form.Label>
                          <Form.Control 
                            type="file" 
                            accept="image/*,application/pdf" 
                            onChange={(e) => updatePassenger(idx, 'passport_img', e.target.files[0])}
                            required
                            isInvalid={!!getFieldError(idx, 'passport_img')}
                          />
                          {getFieldError(idx, 'passport_img') && (
                            <Form.Control.Feedback type="invalid">
                              {getFieldError(idx, 'passport_img')}
                            </Form.Control.Feedback>
                          )}
                          {existingFileNames[`passport_${idx}`] && (
                            <Form.Text className="text-success">
                              <i className="fas fa-check-circle me-1"></i>
                              Current file: {existingFileNames[`passport_${idx}`]}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>CNIC Picture (Optional)</Form.Label>
                          <Form.Control 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => updatePassenger(idx, 'cnic_picture', e.target.files[0])}
                          />
                          {existingFileNames[`cnic_${idx}`] && (
                            <Form.Text className="text-success">
                              <i className="fas fa-check-circle me-1"></i>
                              Current file: {existingFileNames[`cnic_${idx}`]}
                            </Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            Upload a clear photo of the CNIC/ID card
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="form-group mb-3">
                          <Form.Label>Picture (Optional)</Form.Label>
                          <Form.Control 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => updatePassenger(idx, 'picture', e.target.files[0])}
                          />
                          {existingFileNames[`picture_${idx}`] && (
                            <Form.Text className="text-success">
                              <i className="fas fa-check-circle me-1"></i>
                              Current file: {existingFileNames[`picture_${idx}`]}
                            </Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            Upload a clear photo of the passenger
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="form-group mb-0">
                          <Form.Label>Special Requirements</Form.Label>
                          <Form.Control 
                            as="textarea" 
                            rows={2} 
                            placeholder="Any special requirements, dietary needs, medical conditions..."
                            value={passenger.remarks || ''}
                            onChange={(e) => updatePassenger(idx, 'remarks', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )) : (
                  <div className="text-center text-muted py-4">
                    <i className="fas fa-users fa-2x mb-2"></i>
                    <p>No passengers to display</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {step > 1 && (
              <Button 
                variant="outline-secondary" 
                onClick={() => setStep(step - 1)}
                className="me-2"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>
            )}
          </div>
          
          <div className="d-flex align-items-center">
            {step === 2 && (
              <Button 
                variant="success" 
                onClick={handleSubmit} 
                disabled={loading || !isPassengerFormEnabled()}
                className="px-4"
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : !isPassengerFormEnabled() ? (
                  <>
                    <i className="fas fa-lock me-2"></i>
                    Complete Room Configuration First
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle me-2"></i>
                    {existingBooking ? 'Update Booking' : 'Confirm Booking'} - PKR {calculateFinalAmount().toLocaleString()}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
      
      {addAgent && (
        <AddAgent show={addAgent} setShow={setAddAgent} path="booking" />
      )}
    </Modal>
  );
};

export default BookUmrah;